import { useMemo } from 'react';
import { OverlayScrollArea } from '@odo/shared-ui';
import type { VirtualAppointmentTeam } from '../../../services/virtualAppointments';
import { useVirtualAppointmentTypes } from '../../../hooks/useVirtualAppointments';
import { SearchableSelect, type SearchableSelectOption } from '../../shared/SearchableSelect';
import { SegmentedToggle, type SegmentedOption } from '../../virtual-appointments/SegmentedToggle';
import { teamSegmentOption } from '../../virtual-appointments/teamSegmentOptions';
import { PracticeFilterList, type CalendarPractice } from '../appointments/PracticeFilterList';
import { hasActiveOverviewFilters, type OverviewFilters } from './overviewFilters';
import { OVERVIEW_TABS, type OverviewTab } from './overviewTabs';
import { SidebarSection } from './SidebarSection';

type TeamChoice = VirtualAppointmentTeam | 'all';

const TAB_OPTIONS: readonly SegmentedOption<OverviewTab>[] = OVERVIEW_TABS.map((config) => ({
    value: config.value,
    label: config.label
}));

const TEAM_OPTIONS: readonly SegmentedOption<TeamChoice>[] = [
    { value: 'all', label: 'All' },
    teamSegmentOption('DM', 'DM'),
    teamSegmentOption('Clinical', 'Clinical')
];

// Not a type code the lookup can produce, so it can share the option list with the codes.
const CUSTOM_TYPE_VALUE = '__custom';
const CUSTOM_TYPE_OPTION: SearchableSelectOption = {
    value: CUSTOM_TYPE_VALUE,
    label: 'Custom',
    sublabel: 'Free-text types'
};

interface VirtualAppointmentsFilterSidebarProps {
    tab: OverviewTab;
    onTabChange: (tab: OverviewTab) => void;
    filters: OverviewFilters;
    onChange: (next: OverviewFilters) => void;
    onReset: () => void;
}

export function VirtualAppointmentsFilterSidebar({
    tab,
    onTabChange,
    filters,
    onChange,
    onReset
}: VirtualAppointmentsFilterSidebarProps) {
    const { data: types } = useVirtualAppointmentTypes();
    const typeOptions = useMemo(
        () => [
            ...(types ?? []).map((type) => ({
                value: type.code,
                label: type.code,
                sublabel: type.label
            })),
            CUSTOM_TYPE_OPTION
        ],
        [types]
    );
    const selectedTypes = filters.includeCustom
        ? [...filters.typeCodes, CUSTOM_TYPE_VALUE]
        : [...filters.typeCodes];
    const setTypes = (values: string[]) =>
        onChange({
            ...filters,
            typeCodes: values.filter((value) => value !== CUSTOM_TYPE_VALUE),
            includeCustom: values.includes(CUSTOM_TYPE_VALUE)
        });
    const setPractices = (practices: CalendarPractice[]) => onChange({ ...filters, practices });

    return (
        <aside className="w-[300px] shrink-0 border-r border-gray-200 flex flex-col min-h-0">
            <OverlayScrollArea
                className="flex-1 min-h-0"
                contentClassName="px-5 py-5"
                innerClassName="space-y-6"
            >
                <SidebarSection label="Status">
                    <SegmentedToggle<OverviewTab>
                        value={tab}
                        options={TAB_OPTIONS}
                        onChange={onTabChange}
                        ariaLabel="VA status"
                    />
                </SidebarSection>
                <SidebarSection label="Team">
                    <SegmentedToggle<TeamChoice>
                        value={filters.team ?? 'all'}
                        options={TEAM_OPTIONS}
                        onChange={(team) =>
                            onChange({ ...filters, team: team === 'all' ? null : team })
                        }
                        ariaLabel="Team"
                    />
                </SidebarSection>
                <SidebarSection label="Type" count={selectedTypes.length}>
                    <SearchableSelect
                        mode="multiple"
                        label="VA type"
                        placeholder="All types"
                        searchPlaceholder="Search a type"
                        options={typeOptions}
                        selected={selectedTypes}
                        onChange={setTypes}
                    />
                </SidebarSection>
                <div className="pt-6 border-t border-gray-100">
                    <PracticeFilterList selected={[...filters.practices]} onChange={setPractices} />
                </div>
            </OverlayScrollArea>
            {hasActiveOverviewFilters(filters) && (
                <div className="shrink-0 border-t border-gray-200 p-4">
                    <button
                        type="button"
                        onClick={onReset}
                        className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50 transition-colors duration-200"
                    >
                        Reset filters
                    </button>
                </div>
            )}
        </aside>
    );
}
